import { useEffect, useMemo, useState } from 'react';

// O legado armazena IDs e referências como STRING(100), inclusive IDs não numéricos.
export type LegacyQuestionId = string | number;
export type LegacyAction = {
  product_id?: Array<number | string>;
  next_step?: LegacyQuestionId | null;
};
export type LegacyOption = {
  option: string;
  option_type?: string;
  answers?: LegacyAction[];
};
export type LegacyQuestion = {
  id: LegacyQuestionId;
  step: number;
  question: string;
  options: LegacyOption[];
};
export type LegacyAnswers = Record<
  string,
  {
    questionId: LegacyQuestionId;
    value: string;
    productsToAdd: Array<number | string>;
    nextId: LegacyQuestionId | null;
  }
>;

const questionKey = (id: LegacyQuestionId) => String(id).trim();
const inventoryCode = (code: unknown) => {
  if (code == null || (typeof code === 'number' && !Number.isFinite(code)))
    return '';
  const text = String(code).trim();
  return /^\d+$/.test(text) ? text.replace(/^0+(?=\d)/, '') : text;
};
const isInventory = (option: LegacyOption) =>
  option.option_type?.trim().toLowerCase() === 'i';

/** Reconstitui somente o ramo ativo, incluindo verificações automáticas de inventário. */
export const deriveLegacyPath = (
  questions: LegacyQuestion[],
  choices: Record<string, number>,
  initialProductCode?: string
) => {
  const path: LegacyQuestion[] = [];
  const answers: LegacyAnswers = {};
  const visited = new Set<string>();
  const ordered = [...questions].sort((a, b) => a.step - b.step);
  let current: LegacyQuestion | undefined =
    ordered.find((question) => Number(question.step) === 1) ?? ordered[0];
  let invalid = false;
  let complete = ordered.length === 0;
  while (current) {
    const id = questionKey(current.id);
    if (visited.has(id)) {
      invalid = true;
      break;
    }
    visited.add(id);
    const inventory: boolean =
      current.options.length > 0 && current.options.every(isInventory);
    const codes = new Set(
      [
        inventoryCode(initialProductCode),
        ...Object.values(answers)
          .flatMap((answer) => answer.productsToAdd)
          .map(inventoryCode),
      ].filter(Boolean)
    );
    const option: LegacyOption | undefined = inventory
      ? current.options.find((item) => {
          if (!isInventory(item)) return false;
          const required = item.option
            .split(',')
            .map(inventoryCode)
            .filter(Boolean);
          return (
            required.length > 0 && required.every((code) => codes.has(code))
          );
        })
      : current.options[choices[id]];
    if (!inventory) path.push(current);
    if (!option) {
      // Um caminho automático não resolvido não significa catálogo sem perguntas.
      if (inventory) invalid = true;
      break;
    }
    const action: LegacyAction | undefined = option.answers?.[0];
    const next: LegacyQuestion | undefined = action
      ? ordered.find(
          (question) =>
            action.next_step != null &&
            questionKey(question.id) === questionKey(action.next_step)
        )
      : ordered.find(
          (question) => Number(question.step) === Number(current?.step) + 1
        );
    answers[id] = {
      questionId: current.id,
      value: option.option,
      productsToAdd: (option.answers ?? [])
        .flatMap((answer) => answer.product_id ?? [])
        .filter((code) => Boolean(inventoryCode(code))),
      nextId: next?.id ?? null,
    };
    complete = !next;
    current = next;
  }
  return { path, answers, invalid, complete: !invalid && complete };
};

export const useLegacyQuestionnaire = (
  questions: LegacyQuestion[],
  onChange?: (answers: LegacyAnswers) => void,
  initialProductCode?: string
) => {
  const [choices, setChoices] = useState<Record<string, number>>({});
  const [index, setIndex] = useState(0);
  const state = useMemo(
    () => deriveLegacyPath(questions, choices, initialProductCode),
    [questions, choices, initialProductCode]
  );
  const position = Math.min(index, Math.max(state.path.length - 1, 0));
  const current = state.path[position];
  useEffect(() => {
    onChange?.(state.answers);
  }, [state, onChange]);

  const answer = (optionIndex: number) => {
    if (!current || choices[questionKey(current.id)] === optionIndex) return;
    const previous = new Set(
      state.path.slice(0, position).map((question) => questionKey(question.id))
    );
    setChoices({
      ...Object.fromEntries(
        Object.entries(choices).filter(([id]) => previous.has(id))
      ),
      [questionKey(current.id)]: optionIndex,
    });
    setIndex(position);
  };
  return {
    ...state,
    current,
    position,
    choices,
    answer,
    next: () => setIndex((value) => Math.min(value + 1, state.path.length - 1)),
    back: () => setIndex((value) => Math.max(value - 1, 0)),
    reset: () => {
      setChoices({});
      setIndex(0);
    },
    canNext: !state.invalid && position < state.path.length - 1,
    canBack: position > 0,
  };
};
